/**
 * QuotationApprovals — pending approval-matrix requests inbox.
 *
 * The backend filters to the role-appropriate set: admin sees
 * everything, accountants see Finance tier, etc. The page just renders
 * what /api/approval-requests returns and lets the user approve / reject.
 */
import React, { useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Loader2, Inbox } from 'lucide-react';
import ContractRiskCard from '../components/ContractRiskCard';

const fmtSAR = (n) => `${Number(n || 0).toLocaleString()} SAR`;

export default function QuotationApprovals() {
  const [requests, setRequests] = useState(null);
  const [working, setWorking]   = useState({});
  const [note, setNote]         = useState({});

  const load = async () => {
    try {
      const { data } = await axiosInstance.get('/approval-requests');
      setRequests(data);
    } catch (_) {
      toast.error('Failed to load approvals');
      setRequests([]);
    }
  };

  useEffect(() => { load(); }, []);

  const decide = async (rid, decision) => {
    setWorking((w) => ({ ...w, [rid]: true }));
    try {
      await axiosInstance.post(
        `/approval-requests/${rid}/decide`,
        { decision, note: note[rid] || undefined },
      );
      toast.success(decision === 'approve' ? 'Approved' : 'Rejected');
      await load();
    } catch (err) {
      const det = err.response?.data?.detail;
      const msg = typeof det === 'object'
        ? (det.error === 'self_approval_forbidden'
            ? 'You cannot approve your own request'
            : det.error === 'role_not_authorised'
            ? `Only ${det.required_role} can decide this request`
            : det.error)
        : 'Failed to decide';
      toast.error(msg);
    } finally {
      setWorking((w) => ({ ...w, [rid]: false }));
    }
  };

  return (
    <div data-testid="quotation-approvals-page" className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Quotation Approval Inbox</h2>
        <p className="text-sm text-gray-500">
          Quotations awaiting your decision based on the approval matrix.
        </p>
      </div>

      {requests === null ? (
        <div className="text-center py-12 text-gray-500">Loading…</div>
      ) : requests.length === 0 ? (
        <div
          data-testid="quotation-approvals-empty"
          className="text-center py-12 text-gray-400 rounded-xl border border-dashed border-gray-200"
        >
          <Inbox className="w-10 h-10 mx-auto mb-2 opacity-50" />
          No pending approvals for you right now.
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((r) => (
            <div
              key={r.id}
              data-testid={`approval-row-${r.id}`}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-col md:flex-row md:items-center gap-3"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono font-bold text-gray-900">{r.quotation_no}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                    {r.tier?.label || 'tier'}
                  </span>
                  {/* Risk badge — the CFO sees the consolidated risk
                      score BEFORE deciding. Compact mode keeps the row
                      tight; clicking through to the quotation surfaces
                      the full breakdown. */}
                  <ContractRiskCard
                    quotation={{ id: r.quotation_id }}
                    compact
                  />
                  <span className="text-xs text-gray-500">
                    requested by {r.requested_by_name || '—'}
                  </span>
                </div>
                <div className="text-xl font-bold text-indigo-700 mt-1">
                  {fmtSAR(r.amount)}
                </div>
                {/* Escalation reasons — surfaces the routing chain
                    inline so the approver knows WHY this request
                    landed in their queue. */}
                {r.escalated && Array.isArray(r.escalations) && r.escalations.length > 0 && (
                  <div
                    data-testid={`approval-escalations-${r.id}`}
                    className="mt-2 text-xs rounded-md border border-orange-200 bg-orange-50 px-2 py-1.5"
                  >
                    <div className="font-semibold text-orange-900 mb-1">
                      Escalated from {(r.base_tier && r.base_tier.label) || 'auto'} →
                      {' '}{(r.tier && r.tier.label) || 'top tier'}
                    </div>
                    <ul className="space-y-0.5 text-orange-800">
                      {r.escalations.map((esc, i) => (
                        <li key={i}>· {esc.label}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <input
                  data-testid={`approval-note-${r.id}`}
                  type="text"
                  placeholder="Optional decision note…"
                  value={note[r.id] || ''}
                  onChange={(e) => setNote((n) => ({ ...n, [r.id]: e.target.value }))}
                  className="mt-2 w-full text-sm form-input"
                />
              </div>
              <div className="flex gap-2">
                <button
                  data-testid={`approval-approve-${r.id}`}
                  disabled={working[r.id]}
                  onClick={() => decide(r.id, 'approve')}
                  className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm px-3 py-2 rounded-md inline-flex items-center gap-1.5 font-semibold"
                >
                  {working[r.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  Approve
                </button>
                <button
                  data-testid={`approval-reject-${r.id}`}
                  disabled={working[r.id]}
                  onClick={() => decide(r.id, 'reject')}
                  className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm px-3 py-2 rounded-md inline-flex items-center gap-1.5 font-semibold"
                >
                  <XCircle className="w-4 h-4" />
                  Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
